import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import client from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";

interface TeamMember {
  id: string;
  username: string;
  role: "CAPTAIN" | "MEMBER";
  points: number;
  joinedAt: string;
}

interface TeamData {
  id: string;
  name: string;
  description: string | null;
  inviteCode: string;
  members: TeamMember[];
  totalPoints: number;
  memberCount: number;
  rank: number;
}

export default function Team() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [team, setTeam] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [removingMember, setRemovingMember] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  useEffect(() => {
    if (user?.teamId) {
      fetchTeam();
    } else {
      setLoading(false);
    }
  }, [user?.teamId]);

  async function fetchTeam() {
    try {
      setLoading(true);
      const res = await client.get(`/teams/${user?.teamId}`);
      setTeam(res.data?.data || res.data);
    } catch {
      addToast({ type: "error", message: "Failed to load team data." });
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!createName.trim()) return;
    setCreating(true);
    try {
      const res = await client.post("/teams", {
        name: createName.trim(),
        description: createDesc.trim() || undefined,
      });
      addToast({ type: "success", message: "Team created!" });
      setTeam(res.data?.data || res.data);
      setCreateName("");
      setCreateDesc("");
    } catch (err: any) {
      addToast({
        type: "error",
        message: err.response?.data?.message || "Failed to create team.",
      });
    } finally {
      setCreating(false);
    }
  }

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    if (!joinCode.trim()) return;
    setJoining(true);
    try {
      const res = await client.post("/teams/join", {
        inviteCode: joinCode.trim(),
      });
      addToast({ type: "success", message: "Joined team!" });
      setTeam(res.data?.data || res.data);
      setJoinCode("");
    } catch (err: any) {
      addToast({
        type: "error",
        message: err.response?.data?.message || "Failed to join team.",
      });
    } finally {
      setJoining(false);
    }
  }

  async function handleLeave() {
    setLeaving(true);
    try {
      await client.post("/teams/leave");
      addToast({ type: "success", message: "Left team." });
      setTeam(null);
      setConfirmLeave(false);
    } catch (err: any) {
      addToast({
        type: "error",
        message: err.response?.data?.message || "Failed to leave team.",
      });
    } finally {
      setLeaving(false);
    }
  }

  async function handleRemoveMember(memberId: string) {
    setRemovingMember(memberId);
    try {
      await client.delete(`/teams/members/${memberId}`);
      addToast({ type: "success", message: "Member removed." });
      setConfirmRemove(null);
      fetchTeam();
    } catch (err: any) {
      addToast({
        type: "error",
        message: err.response?.data?.message || "Failed to remove member.",
      });
    } finally {
      setRemovingMember(null);
    }
  }

  function copyInviteCode() {
    if (!team?.inviteCode) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(team.inviteCode).then(() => {
        setCopied(true);
        addToast({ type: "success", message: "Invite code copied!" });
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = team.inviteCode;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      addToast({ type: "success", message: "Invite code copied!" });
      setTimeout(() => setCopied(false), 2000);
    }
  }

  const isCaptain = team?.members.some(
    (m) => m.id === user?.id && m.role === "CAPTAIN"
  );

  if (loading) {
    return (
      <div className="ds-flex ds-items-center ds-justify-center ds-min-h-[60vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="ds-w-8 ds-h-8 ds-border-2 ds-border-ds-accent ds-border-t-transparent ds-rounded-full"
        />
      </div>
    );
  }

  if (!user?.teamId && !team) {
    return (
      <div className="ds-max-w-4xl ds-mx-auto ds-px-4 ds-py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="ds-text-center ds-mb-12"
        >
          <h1 className="ds-text-3xl ds-font-bold ds-text-ds-text ds-mb-2">
            Join or Create a Team
          </h1>
          <p className="ds-text-ds-text-dim">
            Team up with others to dominate the leaderboard.
          </p>
        </motion.div>

        <div className="ds-grid ds-grid-cols-1 md:ds-grid-cols-2 ds-gap-6">
          {/* Create Team */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="ds-card ds-p-6"
          >
            <h2 className="ds-text-xl ds-font-semibold ds-text-ds-text ds-mb-4">
              Create Team
            </h2>
            <form onSubmit={handleCreate} className="ds-space-y-4">
              <div>
                <label className="ds-block ds-text-sm ds-font-medium ds-text-ds-text-dim ds-mb-1">
                  Team Name
                </label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  className="ds-input ds-w-full"
                  placeholder="Enter team name"
                  required
                />
              </div>
              <div>
                <label className="ds-block ds-text-sm ds-font-medium ds-text-ds-text-dim ds-mb-1">
                  Description{" "}
                  <span className="ds-text-ds-text-muted">(optional)</span>
                </label>
                <textarea
                  value={createDesc}
                  onChange={(e) => setCreateDesc(e.target.value)}
                  className="ds-input ds-w-full ds-resize-none"
                  rows={3}
                  placeholder="Brief description of your team"
                />
              </div>
              <button
                type="submit"
                disabled={creating || !createName.trim()}
                className="ds-btn ds-btn-primary ds-w-full"
              >
                {creating ? (
                  <span className="ds-flex ds-items-center ds-justify-center ds-gap-2">
                    <span className="ds-w-4 ds-h-4 ds-border-2 ds-border-white ds-border-t-transparent ds-rounded-full ds-animate-spin" />
                    Creating...
                  </span>
                ) : (
                  "Create"
                )}
              </button>
            </form>
          </motion.div>

          {/* Join Team */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="ds-card ds-p-6"
          >
            <h2 className="ds-text-xl ds-font-semibold ds-text-ds-text ds-mb-4">
              Join Team
            </h2>
            <form onSubmit={handleJoin} className="ds-space-y-4">
              <div>
                <label className="ds-block ds-text-sm ds-font-medium ds-text-ds-text-dim ds-mb-1">
                  Invite Code
                </label>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  className="ds-input ds-w-full ds-font-mono"
                  placeholder="Paste invite code"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={joining || !joinCode.trim()}
                className="ds-btn ds-btn-primary ds-w-full"
              >
                {joining ? (
                  <span className="ds-flex ds-items-center ds-justify-center ds-gap-2">
                    <span className="ds-w-4 ds-h-4 ds-border-2 ds-border-white ds-border-t-transparent ds-rounded-full ds-animate-spin" />
                    Joining...
                  </span>
                ) : (
                  "Join"
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!team) return null;

  return (
    <div className="ds-max-w-5xl ds-mx-auto ds-px-4 ds-py-8">
      {/* Team Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="ds-mb-8"
      >
        <div className="ds-flex ds-items-center ds-gap-3 ds-mb-2">
          <h1 className="ds-text-3xl ds-font-bold ds-text-ds-text">
            {team.name}
          </h1>
          <span
            className={`ds-px-3 ds-py-1 ds-rounded-full ds-text-xs ds-font-semibold ${
              isCaptain
                ? "ds-bg-ds-accent/20 ds-text-ds-accent"
                : "ds-bg-ds-bg-secondary ds-text-ds-text-dim"
            }`}
          >
            {isCaptain ? "CAPTAIN" : "MEMBER"}
          </span>
        </div>
        {team.description && (
          <p className="ds-text-ds-text-dim">{team.description}</p>
        )}
      </motion.div>

      {/* Team Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="ds-grid ds-grid-cols-3 ds-gap-4 ds-mb-8"
      >
        <div className="ds-card ds-p-4 ds-text-center">
          <div className="ds-text-sm ds-text-ds-text-dim ds-mb-1">
            Total Points
          </div>
          <div className="ds-text-2xl ds-font-bold ds-font-mono ds-text-ds-accent">
            {team.totalPoints.toLocaleString()}
          </div>
        </div>
        <div className="ds-card ds-p-4 ds-text-center">
          <div className="ds-text-sm ds-text-ds-text-dim ds-mb-1">Members</div>
          <div className="ds-text-2xl ds-font-bold ds-font-mono ds-text-ds-text">
            {team.memberCount}
          </div>
        </div>
        <div className="ds-card ds-p-4 ds-text-center">
          <div className="ds-text-sm ds-text-ds-text-dim ds-mb-1">Rank</div>
          <div className="ds-text-2xl ds-font-bold ds-font-mono ds-text-ds-text">
            #{team.rank}
          </div>
        </div>
      </motion.div>

      {/* Invite Code */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="ds-card ds-p-6 ds-mb-8"
      >
        <h2 className="ds-text-lg ds-font-semibold ds-text-ds-text ds-mb-3">
          Invite Code
        </h2>
        <div className="ds-flex ds-items-center ds-gap-3">
          <div className="ds-flex-1 ds-bg-ds-bg-secondary ds-rounded-lg ds-px-4 ds-py-3 ds-font-mono ds-text-ds-text ds-select-all ds-border ds-border-ds-border">
            {team.inviteCode}
          </div>
          <button
            onClick={copyInviteCode}
            className="ds-btn ds-btn-primary"
          >
            {copied ? (
              <span className="ds-flex ds-items-center ds-gap-1">
                <svg
                  className="ds-w-4 ds-h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Copied!
              </span>
            ) : (
              "Copy"
            )}
          </button>
        </div>
      </motion.div>

      {/* Members List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="ds-card ds-p-6 ds-mb-8"
      >
        <h2 className="ds-text-lg ds-font-semibold ds-text-ds-text ds-mb-4">
          Members
        </h2>
        <div className="ds-space-y-3">
          <AnimatePresence>
            {team.members
              .slice()
              .sort((a, b) => {
                if (a.role === "CAPTAIN") return -1;
                if (b.role === "CAPTAIN") return 1;
                return b.points - a.points;
              })
              .map((member, idx) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: idx * 0.05 }}
                  className="ds-flex ds-items-center ds-justify-between ds-p-4 ds-rounded-lg ds-bg-ds-bg-secondary ds-border ds-border-ds-border"
                >
                  <div className="ds-flex ds-items-center ds-gap-4">
                    <div className="ds-w-10 ds-h-10 ds-rounded-full ds-bg-ds-accent/20 ds-flex ds-items-center ds-justify-center ds-text-ds-accent ds-font-semibold">
                      {member.username.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="ds-font-medium ds-text-ds-text">
                        {member.username}
                      </div>
                      <div className="ds-text-xs ds-text-ds-text-muted">
                        Joined{" "}
                        {new Date(member.joinedAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="ds-flex ds-items-center ds-gap-4">
                    <span className="ds-font-mono ds-text-sm ds-text-ds-accent">
                      {member.points.toLocaleString()} pts
                    </span>
                    <span
                      className={`ds-px-2 ds-py-1 ds-rounded ds-text-xs ds-font-medium ${
                        member.role === "CAPTAIN"
                          ? "ds-bg-ds-accent/20 ds-text-ds-accent"
                          : "ds-bg-ds-bg-primary ds-text-ds-text-dim"
                      }`}
                    >
                      {member.role}
                    </span>

                    {isCaptain && member.id !== user?.id && (
                      <div className="ds-relative">
                        {confirmRemove === member.id ? (
                          <div className="ds-flex ds-items-center ds-gap-2">
                            <span className="ds-text-xs ds-text-ds-danger">
                              Remove?
                            </span>
                            <button
                              onClick={() => handleRemoveMember(member.id)}
                              disabled={removingMember === member.id}
                              className="ds-text-xs ds-text-ds-danger ds-hover:ds-underline"
                            >
                              {removingMember === member.id
                                ? "..."
                                : "Yes"}
                            </button>
                            <button
                              onClick={() => setConfirmRemove(null)}
                              className="ds-text-xs ds-text-ds-text-dim ds-hover:ds-underline"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmRemove(member.id)}
                            className="ds-text-xs ds-text-ds-danger/60 ds-hover:ds-text-ds-danger ds-transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Leave Team */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="ds-card ds-p-6"
      >
        <h2 className="ds-text-lg ds-font-semibold ds-text-ds-text ds-mb-3">
          Leave Team
        </h2>
        <p className="ds-text-sm ds-text-ds-text-dim ds-mb-4">
          {isCaptain
            ? "As captain, you must transfer ownership or dissolve the team before leaving."
            : "Are you sure you want to leave this team? You'll need a new invite code to rejoin."}
        </p>

        {!confirmLeave ? (
          <button
            onClick={() => setConfirmLeave(true)}
            disabled={isCaptain}
            className="ds-btn ds-btn-danger"
          >
            Leave Team
          </button>
        ) : (
          <div className="ds-flex ds-items-center ds-gap-3">
            <span className="ds-text-sm ds-text-ds-danger">
              Are you sure?
            </span>
            <button
              onClick={handleLeave}
              disabled={leaving}
              className="ds-btn ds-btn-danger ds-text-sm"
            >
              {leaving ? (
                <span className="ds-flex ds-items-center ds-gap-2">
                  <span className="ds-w-3 ds-h-3 ds-border-2 ds-border-white ds-border-t-transparent ds-rounded-full ds-animate-spin" />
                  Leaving...
                </span>
              ) : (
                "Yes, Leave"
              )}
            </button>
            <button
              onClick={() => setConfirmLeave(false)}
              className="ds-btn ds-btn-ghost ds-text-sm"
            >
              Cancel
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
